import { FC, useState } from 'react';
import dayjs from 'dayjs';
import { AddOutlined, DeleteOutline, InfoOutlined } from '@mui/icons-material';

import { CustomButton, Picker, Text } from '../../shared/uiComponents';
import { dateNormalizer } from '../../shared/Helpers/functions';
import { RED, TEXT } from '../../shared/Helpers/colors';
import { RecurringScheduleTimeSlot, RecurringScheduleViewModel, UpdateRecurringSchedulePayload } from '../../redux/API/ClientAPIHelpers/recurringSchedule';
import { WEEKDAYS } from './CreateScheduleModal/constants';

interface EditScheduleModalProperties {
	authorizationEndDate: string | null;
	isAdmin?: boolean;
	onClose: () => void;
	onSave: (payload: UpdateRecurringSchedulePayload) => Promise<void>;
	schedule: RecurringScheduleViewModel;
}

const toTimeValue = (minutes: number) => dayjs('2000-01-01').startOf('day').add(minutes, 'minute');
const EditScheduleModal: FC<EditScheduleModalProperties> = ({ authorizationEndDate, isAdmin, onClose, onSave, schedule }) => {
	const [startDate, setStartDate] = useState(() => schedule.startDate && dayjs(schedule.startDate).isValid() ? dateNormalizer(dayjs(schedule.startDate)) : '');
	const [endDate, setEndDate] = useState(() => schedule.endDate && dayjs(schedule.endDate).isValid() ? dateNormalizer(dayjs(schedule.endDate)) : '');
	const [slots, setSlots] = useState(() => schedule.timeSlots.map((slot) => ({ ...slot })));
	const [error, setError] = useState('');
	const [saving, setSaving] = useState(false);
	const [confirmingStartDate, setConfirmingStartDate] = useState(false);

	const validate = (): string => {
		if (isAdmin && startDate) {
			const selectedStart = dayjs(startDate);
			if (!selectedStart.isValid()) return 'Invalid start date.';
		}
		const effectiveStart = startDate ? dayjs(startDate) : dayjs(schedule.startDate);
		if (endDate) {
			const selected = dayjs(endDate);
			if (!selected.isValid()) return 'Invalid date.';
			if (selected.isBefore(effectiveStart)) return 'End date cannot be before start date.';
			if (authorizationEndDate && selected.isAfter(dayjs(authorizationEndDate))) return `End date cannot exceed authorization expiration (${dayjs(authorizationEndDate).format('MMM D, YYYY')}).`;
		}
		if (!slots.length) return 'At least one day/time slot is required.';
		if (slots.some((slot) => slot.dayOfWeek < 0 || slot.dayOfWeek > 6 || slot.startTimeMinutes >= slot.endTimeMinutes)) return 'Every time slot must have an end time after its start time.';
		const overlaps = WEEKDAYS.some((day) => {
			const daySlots = slots.filter((slot) => slot.dayOfWeek === day.code).sort((left, right) => left.startTimeMinutes - right.startTimeMinutes);
			return daySlots.some((slot, index) => index > 0 && daySlots[index - 1].endTimeMinutes > slot.startTimeMinutes);
		});
		if (overlaps) return 'Times on the same day cannot overlap.';
		return '';
	};

	const updateSlot = (index: number, value: Partial<RecurringScheduleTimeSlot>) =>
		setSlots((current) => current.map((slot, slotIndex) => slotIndex === index ? { ...slot, ...value } : slot));
	const toggleDay = (dayOfWeek: number) => setSlots((current) => current.some((slot) => slot.dayOfWeek === dayOfWeek)
		? current.filter((slot) => slot.dayOfWeek !== dayOfWeek)
		: [...current, { dayOfWeek, startTimeMinutes: 540, endTimeMinutes: 600 }]);
	const addTime = (dayOfWeek: number) => setSlots((current) => [...current, { dayOfWeek, startTimeMinutes: 540, endTimeMinutes: 600 }]);

	const isStartDateChanged = Boolean(isAdmin && startDate && startDate !== dateNormalizer(dayjs(schedule.startDate)));

	const submitSave = () => {
		setSaving(true);
		onSave({ ...(isStartDateChanged ? { startDate } : {}), endDate: endDate || null, timeSlots: slots }).finally(() => setSaving(false));
	};

	const handleSave = () => {
		const validationError = validate();
		if (validationError) {
			setError(validationError);
			return;
		}
		if (isStartDateChanged && !confirmingStartDate) {
			setConfirmingStartDate(true);
			return;
		}
		submitSave();
	};

	return (
		<div className="editEndDateContent">
			<div className="editScheduleNotice">
				<InfoOutlined fontSize="small" />
				<Text title="These edits correct the full schedule history. For a time change that starts in the future, end this schedule and create a new one." type="pxs" color={TEXT.SECONDARY} />
			</div>
			{isAdmin && (
				<Picker.CustomDate
					label="Start Date"
					value={startDate ? dayjs(startDate) : null}
					onChange={(value) => { setStartDate(value ? dateNormalizer(value) : ''); setError(''); setConfirmingStartDate(false); }}
				/>
			)}
			<Picker.CustomDate
				label="End Date (optional)"
				value={endDate ? dayjs(endDate) : null}
				onChange={(value) => { setEndDate(value ? dateNormalizer(value) : ''); setError(''); }}
				description="Leave blank to continue through the authorization period"
			/>
			<div className="editScheduleSlots">
				<div className="editScheduleSectionIntro">
					<Text title="Weekly schedule" type="psm" fontWeight="semiBold" />
					<Text title="Select each day this schedule repeats. Add more than one time when a day has multiple sessions." type="pxs" color={TEXT.TERTIARY} />
				</div>
				<div className="editScheduleDayToggles">
					{WEEKDAYS.map((day) => {
						const enabled = slots.some((slot) => slot.dayOfWeek === day.code);
						return <button key={day.code} type="button" className={`editScheduleDayToggle ${enabled ? 'active' : ''}`} aria-pressed={enabled} onClick={() => toggleDay(day.code)}>{day.short}</button>;
					})}
				</div>
				{WEEKDAYS.filter((day) => slots.some((slot) => slot.dayOfWeek === day.code)).map((day) => (
					<div className="editScheduleSlot" key={day.code}>
						<div className="editScheduleSlotHeader">
							<Text title={day.long} type="psm" fontWeight="semiBold" />
							<button type="button" className="editScheduleRemoveDay" onClick={() => toggleDay(day.code)}>Remove day</button>
						</div>
						{slots.map((slot, index) => slot.dayOfWeek !== day.code ? null : (
							<div className="editScheduleTimeRow" key={slot.id ?? `${day.code}-${index}`}>
								<Picker.CustomTime label="Start time" value={toTimeValue(slot.startTimeMinutes)} onChange={(value) => value && updateSlot(index, { startTimeMinutes: value.hour() * 60 + value.minute() })} />
								<Picker.CustomTime label="End time" value={toTimeValue(slot.endTimeMinutes)} onChange={(value) => value && updateSlot(index, { endTimeMinutes: value.hour() * 60 + value.minute() })} />
								<button type="button" className="editScheduleRemove" aria-label={`Remove ${day.long} time ${index + 1}`} onClick={() => setSlots((current) => current.filter((_, slotIndex) => slotIndex !== index))}><DeleteOutline fontSize="small" /></button>
							</div>
						))}
						<button type="button" className="editScheduleAddTime" onClick={() => addTime(day.code)}><AddOutlined fontSize="small" /> Add another time</button>
					</div>
				))}
			</div>
			{confirmingStartDate && (
				<div className="editScheduleNotice" style={{ borderColor: 'rgba(235, 148, 33, 0.5)', background: 'rgba(235, 148, 33, 0.06)' }}>
					<InfoOutlined fontSize="small" style={{ color: 'rgba(235, 148, 33, 1)' }} />
					<Text title="Changing the start date rewrites the full schedule history and may affect report metrics. Are you sure?" type="pxs" color={TEXT.SECONDARY} />
				</div>
			)}
			{Boolean(error) && <Text title={error} type="pxs" color={RED.BASE} className="editEndDateError" />}
			<div className="editEndDateActions">
				<CustomButton title="Cancel" buttonType="secondaryGray" sizeType="lg" onClick={onClose} disabled={saving} />
				<CustomButton title={confirmingStartDate ? 'Confirm & Save' : 'Save Changes'} buttonType="primary" sizeType="lg" onClick={handleSave} loading={saving} />
			</div>
		</div>
	);
};

export default EditScheduleModal;
